import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Skeleton } from "antd";

import ManufacturerHomeTab from "./ManufacturerHomeTab";
import ManufacturerDetailsTab from "./ManufacturerDetailsTab";
import ManufacturerInventoryTab from "./ManufacturerInventoryTab";
import ManufacturerContractsDashboard from "../ManufacturerCommissionContract/ManufacturerContractsDashboard";

import APIService from "../../Service/APIService";
import TitleBar from "../../common/title-bar/title-bar";
import Button from "../../common/button/button";
import Text from "../../common/typography/text";
import { timeframeButtons } from "../Utils/Constants";
import { useAuth } from "../AuthProvider.js";
import { hasPermission } from "../../Config/permissions.js";
import TabFade from "../../common/tab-fade";
import "./Manufacturers.css";

const ManufacturerDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const userData = user;
  const isCreateMode = !id;
  const [manufacturerDetails, setManufacturerDetails] = useState(null);

  const [filterButton, setFilterButton] = useState("YTD");


  const queryParams = new URLSearchParams(location.search);
  const requestedTab = queryParams.get("tab");
  const manufacturerNameFromQuery = (queryParams.get("manufacturerName") || "").trim();
  const defaultSegment =
    isCreateMode || requestedTab === "details"
      ? "Manufacturer Details"
      : requestedTab === "inventory"
        ? "Inventory Page"
        : "Manufacturer Home";

  const [selectedSegment, setSelectedSegment] = useState(defaultSegment);

  useEffect(() => {
    setSelectedSegment(defaultSegment);
  }, [defaultSegment]);

  /**
   * Auth + fetch
   */
  const fetchManufacturerDetails = useCallback(async () => {
    if (isCreateMode) {
      const createDefaults = {
        ManufacturerName: manufacturerNameFromQuery,
        IsActive: true,
        NeedsApproval: true,
        CollectPayment: false,
        ManufacturerVisibilityDO: false,
        ManufacturerVisibilityCO: false,
        ManufacturerVisibilityPU: false,
      };
      setManufacturerDetails(createDefaults);
      return createDefaults;
    }
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend(
      `getmanufacturerbyid?ID=${id}`
    );
    setManufacturerDetails(response?.data?.data);
    return response?.data?.data;
  }, [id, isCreateMode, manufacturerNameFromQuery]);

  useEffect(() => {
    if (userData?.Type === "Account") {
      window.location.href = "/";
      return;
    }

    fetchManufacturerDetails();
  }, [fetchManufacturerDetails, userData?.Type]);

  const entityNameValue = useMemo(() => {
    if (!manufacturerDetails) {
      return (
        <Skeleton.Input active style={{ width: 60, height: 16 }} />
      );
    }
    return "Manufacturers";
  }, [manufacturerDetails]);

  const labelValue = useMemo(() => {
    if (!manufacturerDetails) {
      return (
        <Skeleton.Input active style={{ width: 260, height: 20 }} />
      );
    }
    return manufacturerDetails.ManufacturerName;
  }, [manufacturerDetails]);

  /**
   * Handlers
   */
  const handleSegmentChange = (value) => {
    setSelectedSegment(value);
  };

  const handleFilterButtonChange = (value) => {
    setFilterButton(value);
  };

  return (
    <div>
      <TitleBar
        entityName={entityNameValue}
        label={labelValue}
        statusIndicatorStatus={
          manufacturerDetails
            ? manufacturerDetails.IsActive
              ? "Active"
              : "Inactive"
            : ""
        }
        statusIndicatorStatusType={
          manufacturerDetails
            ? manufacturerDetails.IsActive
              ? "success"
              : "failure"
            : ""
        }
        filterBarComponents={
          <>
            {!isCreateMode && (
              <Button
                isActive={selectedSegment === "Manufacturer Home"}
                variant="timeframe"
                onClick={() => handleSegmentChange("Manufacturer Home")}
              >
                Home
              </Button>
            )}
            <Button
              isActive={selectedSegment === "Manufacturer Details"}
              variant="timeframe"
              onClick={() => handleSegmentChange("Manufacturer Details")}
            >
              Details
            </Button>
            {!isCreateMode && (
              <Button
                isActive={selectedSegment === "Inventory Page"}
                variant="timeframe"
                onClick={() => handleSegmentChange("Inventory Page")}
              >
                Inventory Page
              </Button>
            )}

            {!isCreateMode &&
              (userData?.Type === "Admin" ||
                userData?.Type === "ASD" ||
                userData?.Type === "Authorizer" ||
                userData?.Type === "Viewer") &&
              hasPermission(userData, "mfr_contract.view") && (
                <Button
                  isActive={selectedSegment === "Manufacturer Contracts"}
                  variant="timeframe"
                  onClick={() =>
                    handleSegmentChange("Manufacturer Contracts")
                  }
                >
                  Manufacturer Contracts
                </Button>
              )}
          </>
        }
        rightFilterBarComponents={
          !isCreateMode && selectedSegment === "Manufacturer Home" && (
            <>
              <Text variant="buttonText">Timeframe</Text>
              {timeframeButtons.map((btn) => (
                <Button
                  key={btn}
                  isActive={filterButton === btn}
                  variant="timeframe"
                  onClick={() => handleFilterButtonChange(btn)}
                >
                  {btn}
                </Button>
              ))}
            </>
          )
        }
      />

      <TabFade activeKey={selectedSegment} style={{ marginTop: "20px" }}>
        {selectedSegment === "Manufacturer Home" && (
          <ManufacturerHomeTab
            manufacturerDetails={manufacturerDetails}
            filterButton={filterButton}
          />
        )}

        {selectedSegment === "Manufacturer Details" && (
          <ManufacturerDetailsTab
            manufacturerDetails={manufacturerDetails}
            onManufacturerUpdated={fetchManufacturerDetails}
          />
        )}

        {selectedSegment === "Inventory Page" && (
          <ManufacturerInventoryTab />
        )}

        {(userData?.Type === "Admin" ||
          userData?.Type === "ASD" ||
          userData?.Type === "Authorizer" ||
          userData?.Type === "Viewer") &&
          hasPermission(userData, "mfr_contract.view") &&
          selectedSegment === "Manufacturer Contracts" && (
            <ManufacturerContractsDashboard />
          )}
      </TabFade>
    </div>
  );
};

export default ManufacturerDetails;
